/**
 * The metrics that belong to **PD** rather than to Fundamentals or Universe Rank.
 *
 * ## Why a list here and not a category from the server
 *
 * The chart gates these by their category, which is the server's job and the
 * only enforcement that counts: `routers/qv.py` resolves an alias to a family
 * and asks for `ind:{category}`. The two surfaces below are different — both
 * receive the numbers whatever the caller holds, and can only decline to draw
 * them:
 *
 * * **the financials panel** reads `/sym/fundamentals/{symbol}`, which is
 *   unauthenticated by design — its docstring says so, which is why a refresh is
 *   a CLI;
 * * **the screener** pushes *all* columns and treats show/hide as a client-side
 *   view, which `routers/scr.py` states at the top of the file.
 *
 * So this is a rendering rule, not a gate. Closing either properly means the
 * server dropping the values per caller, and that is a `qv-data` decision.
 *
 * ## Listed by id, because the category the client can see is the wrong one
 *
 * The categories that travel with a metric are the *panel's* — Compounding,
 * Scorecard, Ranks — and those group metrics by what they measure, not by what
 * they cost. `composite` is the clearest case: the panel files it under Ranks
 * and the chart picker offers it under Universe Rank, and neither says anything
 * about who may see it.
 */

/**
 * Field ids withheld from an account that does not hold `ind:PD`.
 *
 * `compositeRaw` rides with `composite`: the percentile and the quantity behind
 * it are the same reading, and hiding one while offering the other withholds
 * nothing.
 *
 * `dividendStreak` — "Years of rises" in the panel's Growth column — was
 * considered and stays in Fundamentals. It is a *dividend* streak, a fact about
 * a payout rather than one of the earnings-pace readings the rest share.
 */
export const PD_METRICS: ReadonlySet<string> = new Set([
  "composite",
  "compositeRaw",
  "consistency",
  "growthBox",
  "pace",
  "paceRaw",
  "yearsToDouble",
]);

/** Whether this field is PD's to withhold. */
export function isPdField(field: string): boolean {
  return PD_METRICS.has(field);
}

/**
 * The rows of one section, less anything PD withholds.
 *
 * `held` rather than a call to `can()` inside, so a caller reading the list many
 * times over one render asks the question once.
 */
export function withoutPd<T extends { id: string }>(rows: readonly T[], held: boolean): T[] {
  return held ? [...rows] : rows.filter((row) => !PD_METRICS.has(row.id));
}
